import { nowIso } from './domain/time.js';
import { newId } from './domain/ids.js';
/** Apply a master-data snapshot to the replicas. Idempotent; safe to re-run. */
export async function applyProvisioningSnapshot(db, snapshot) {
    const p = snapshot.property;
    if (p.id !== db.context.propertyId) {
        throw new Error(`Snapshot is for property ${p.id} but this device is bound to ${db.context.propertyId}`);
    }
    const now = nowIso();
    await db.driver.batch([
        { sql: 'DELETE FROM property' },
        {
            sql: `INSERT INTO property
              (id, name, legal_name, address, city, pincode, phone, email, gstin, state_code,
               fssai, currency, timezone, settings_json, updated_at)
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            params: [
                p.id,
                p.name,
                p.legalName ?? null,
                p.address ?? null,
                p.city ?? null,
                p.pincode ?? null,
                p.phone ?? null,
                p.email ?? null,
                p.gstin,
                p.stateCode,
                p.fssai ?? null,
                p.currency ?? 'INR',
                p.timezone ?? 'Asia/Kolkata',
                p.settingsJson ?? '{}',
                now,
            ],
        },
        // Portable update-then-insert pairs (minSdk 26 SQLite has no upsert).
        ...snapshot.users.flatMap((u) => [
            {
                sql: 'UPDATE users SET name = ?, email = ?, is_active = ?, role = ? WHERE id = ?',
                params: [u.name, u.email, u.isActive ? 1 : 0, u.role, u.id],
            },
            {
                sql: `INSERT INTO users (id, name, email, is_active, role)
              SELECT ?, ?, ?, ?, ? WHERE (SELECT changes()) = 0`,
                params: [u.id, u.name, u.email, u.isActive ? 1 : 0, u.role],
            },
        ]),
        {
            sql: 'DELETE FROM entitlement',
        },
        {
            sql: `INSERT INTO entitlement (id, is_entitled, plan_name, grace_until, as_of)
            VALUES (1, ?, ?, ?, ?)`,
            params: [
                snapshot.entitlement?.isEntitled === false ? 0 : 1,
                snapshot.entitlement?.planName ?? null,
                snapshot.entitlement?.graceUntil ?? null,
                now,
            ],
        },
    ]);
    await db.catalog.upsertCategories(snapshot.categories);
    await db.catalog.upsertMenuItems(snapshot.menuItems);
    await db.tables.upsertTables(snapshot.tables);
    await db.stays.replaceAll(snapshot.stays);
}
/** DEV/TEST ONLY: a small fictional property. Never ships to production. */
export function devSnapshot(propertyId) {
    const catFood = newId();
    const catBeverage = newId();
    return {
        property: {
            id: propertyId,
            name: 'Dev Test Kitchen',
            gstin: '29ABCDE1234F1Z5',
            stateCode: '29',
            address: '1 Test Lane',
            city: 'Bengaluru',
            timezone: 'Asia/Kolkata',
        },
        users: [
            { id: newId(), name: 'Dev Cashier', email: 'cashier@dev.local', isActive: true, role: 'CASHIER' },
            { id: newId(), name: 'Dev Manager', email: 'manager@dev.local', isActive: true, role: 'MANAGER' },
        ],
        categories: [
            { id: catFood, name: 'Mains', sortOrder: 1 },
            { id: catBeverage, name: 'Beverages', sortOrder: 2 },
        ],
        menuItems: [
            {
                id: newId(),
                categoryId: catFood,
                sku: 'FF001',
                name: 'Paneer Butter Masala',
                pricePaise: 28_000,
                gstRateBp: 500,
                hsn: '996331',
                station: 'KITCHEN',
                isVegetarian: true,
                discountPctBp: 0,
                foodCostPctBp: 3200,
                isActive: true,
                updatedAt: nowIso(),
            },
            {
                id: newId(),
                categoryId: catBeverage,
                sku: 'FF002',
                name: 'Masala Chai',
                pricePaise: 4_000,
                gstRateBp: 500,
                hsn: '996331',
                station: 'BEVERAGE',
                isVegetarian: true,
                discountPctBp: 1000,
                foodCostPctBp: 1500,
                isActive: true,
                updatedAt: nowIso(),
            },
        ],
        tables: [
            { id: newId(), code: 'T1', seats: 4, section: 'Main', isActive: true },
            { id: newId(), code: 'T2', seats: 2, section: 'Main', isActive: true },
        ],
        stays: [],
    };
}
//# sourceMappingURL=provisioning.js.map